// App.tsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate} from 'react-router-dom';
import { useConfig } from './ConfigContext'; // Import the useConfig hook
import MapScreen from './components/Screens/MapScreen';
import AnalyticsScreen from './components/Screens/MainAnalytics';
import ProfileScreen from './components/Screens/ProfileScreen';
import MainLayout from './MainLayout';
import MainDisplay from './components/Screens/MainDisplay';
import { useOptionalAuth0 } from './hooks/useOptionalAuth0';
import { ResponsiveProvider } from './contexts/ResponsiveContext';
import ProtectedRoute from './routes/ProtectedRoute';
import { setTokenGetter } from './api/authToken';


const App: React.FC = () => {
   const { isLoading, error, getAccessTokenSilently } = useOptionalAuth0(); // Get loading and error state from context
   const { updateConfig } = useConfig(); // Get the updateConfig function from context

   useEffect(() => {
      setTokenGetter(async () => {
         try {
            return await getAccessTokenSilently({
               authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
               },
            });
         } catch (err) {
            console.error("[Auth0] getAccessTokenSilently failed:", err);
            return null;
         }
      });
      return () => setTokenGetter(null);
   }, [getAccessTokenSilently]);

   useEffect(() => {
      // Update the configuration with values from the .env file
      const mapApiKey = process.env.REACT_APP_MAPTILER_API_KEY;
      if (mapApiKey) {
         updateConfig('MAPTILER_API_KEY', process.env.REACT_APP_MAPTILER_API_KEY);
      }
      document.title = 'Seasight';
   }, [updateConfig]);

   const [visibleWidgets, setVisibleWidgets] = useState({
      alerts: true,
      layers: true,
      legend: true,
   });

   const onWidgetToggle = (widget: "alerts" | "layers" | "legend", isVisible: boolean) => {
      setVisibleWidgets(prev => ({
         ...prev,
         [widget]: isVisible,
      }));
   }

   return (
      <ResponsiveProvider>
         <Routes>
            {/* Redirect root "/" to /map after login */}
            <Route 
               path="/" 
               element={<ProtectedRoute component={MainLayout} onWidgetToggle={onWidgetToggle} visibleWidgets={visibleWidgets}/>} >
               
               <Route index element={<Navigate to="map" replace />} /> 

               <Route
                  path="map"
                  element={<ProtectedRoute component={MainDisplay} activeControl="map" />}
               />

               <Route
                  path="analytics"
                  element={
                     <ProtectedRoute
                     component={AnalyticsScreen}
                     regionId="LSG000001"
                     setRegionId={() => {
                        throw new Error("Function not implemented.");
                     }}
                     />
                  }
               />

               <Route 
                  path="profile"  
                  element={<ProtectedRoute 
                              component={ProfileScreen}
                           />} 
               />
            </Route>
         </Routes>
      </ResponsiveProvider>
   );
};

export default App;